import { TimezoneUtilsService } from './../../shared/utils/timezone-utils.service';
import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { Subject } from 'rxjs/Subject';
import * as moment from 'moment';


import { DownloadResponse } from './../../downloads/model/download-reponse.interface';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { StartAndEndDate } from '../models/download-playout.interface';
import { environment } from 'src/environments/environment';
import { AudioSizeEnum } from 'src/app/shared/model/audio-size.enum';
import { AudioSource } from '../../shared/model/audio-source.interface';

/**
 * @title Table with selection
 */
@Component({
  selector: 'app-linear-playout-table',
  styleUrls: ['linear-playout-table.component.scss'],
  templateUrl: 'linear-playout-table.component.html',
})
export class LinearPlayoutTableComponent implements OnInit, OnDestroy {
  @Input() dataList: Observable<AudioSource[]>;
  @Input() listDownload: Observable<DownloadResponse[]>;
  @Input() finishDownload: Observable<AudioSource>;
  @Input() loading: Observable<boolean>;
  @Input() selectLines: Observable<{ startDateTime: string, endDateTime: string }>;
  @Input() disableAllDownloadButton: Observable<boolean>;

  @Output() callbackDownload: EventEmitter<AudioSource> = new EventEmitter<AudioSource>();
  @Output() directDownload: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectedLines: EventEmitter<AudioSource[]> = new EventEmitter<AudioSource[]>();


  displayedColumns: string[] = [
    'select',
    'airTime',
    'type',
    'eventName',
    'duration',
    'download',
  ];
  dataSource = new MatTableDataSource<AudioSource>();
  selection: SelectionModel<AudioSource>;
  dialogRef: MatDialogRef<ConfirmationDialogComponent>;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(public dialog: MatDialog, private timezoneUtilsService: TimezoneUtilsService) {
  }
  ngOnDestroy(): void {
    this.dataSource = new MatTableDataSource<AudioSource>();
    this.selection = new SelectionModel<AudioSource>(true, []);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();

  }

  ngOnInit() {
    this.dataList.pipe(takeUntil(this.ngUnsubscribe)).subscribe(list => {
      this.dataSource = new MatTableDataSource<AudioSource>(list.map((audio, index) => {
        audio.position = index;
        audio.nameButton = this.checkNameButton(audio);
        audio.isDownloadDisabled = this.isDownloadDisabled(audio.type);
        return audio;
      }));
      this.selection = new SelectionModel<AudioSource>(true, []);

    });

    this.finishDownload?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((line: AudioSource) => {
      if (line != null) {
        this.dataSource.data.forEach((lineSource: AudioSource) => {
          if (lineSource.id === line.id && !line.isTwoAudio) {
            lineSource.isLoading = line.isLoading;
          }

          if (lineSource.id === line.id && line.isTwoAudio) {
            lineSource.isTwoAudio = true;

          }
        });

      }
    });

    this.listDownload?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((downloadList: DownloadResponse[]) => {
      if (downloadList != null) {
        this.dataSource.data.forEach((lineSource: AudioSource) => {
          const line = downloadList.filter(download => {
            const startDate = this.timezoneUtilsService.getUserTimeZone(
              this.timezoneUtilsService.addSecondsToDate(download.startDate, environment.downloadPaddingInSeconds));
            const endDate = this.timezoneUtilsService.getUserTimeZone(
              this.timezoneUtilsService.subtractSecondsToDate(download.endDate, environment.downloadPaddingInSeconds));
            const linearStart = moment(lineSource.startTime).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
            const linearEnd = moment(lineSource.endTime).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
            return linearStart === startDate &&
              linearEnd === endDate &&
              +lineSource.stationId === download.stationId;
          });

          if (line.length) {
            lineSource.linkDownload = line.filter(l => l.downloadLink != null).map(l => l.downloadLink);
            lineSource.isLoading = lineSource.linkDownload.length > 0 ?
              lineSource.isTwoAudio != null && lineSource.isTwoAudio && line.length < 2 : true;
            lineSource.nameButton = this.checkNameButton(lineSource);
          }

        });

      }
    });

    this.selectLines?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((line: StartAndEndDate) => {
      if (line != null) {
        const lineOverlap: AudioSource = { startTime: line.startDateTime, endTime: line.endDateTime };
        this.dataSource.data.forEach((lineSource: AudioSource) => {

          if (this.isSubSetOf(lineSource, line) || this.overlapDateTime(lineSource, lineOverlap)) {
            this.selection.select(lineSource);
          } else {
            this.selection.deselect(lineSource);
          }
        });

        this.nextLineIsOverLapTime({
          ...this.selection.selected[this.selection.selected.length - 1],
          startTime: line.startDateTime, endTime: line.endDateTime
        });

      } else {
        this.selection.clear();
      }
    });
    this.disableAllDownloadButton?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((disable: boolean) => {
      if (disable != null) {
        this.dataSource.data.forEach((lineSource: AudioSource) => {
          lineSource.isDownloadDisabled = disable;
        });
      }
    });
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.selectedLines.emit([]);
      return;
    }

    this.selection.select(...this.dataSource.data);
    this.selectedLines.emit(this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: AudioSource): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
  }

  checkBoxClicked(row?: any) {
    this.selection.toggle(row);
    const { lineBeforeSelected, lineAfterSelected } =
      this.checkLineBeforeAndAfter(row);

    if (this.selection.isSelected(row)) {
      if (!lineBeforeSelected && !lineAfterSelected) {
        if (this.selection.selected.length > 1) {
          this.selection.clear();
          this.selection.toggle(row);
        }
      }

    }

    if (
      !this.selection.isSelected(row) &&
      lineBeforeSelected &&
      lineAfterSelected
    ) {
      for (const value of this.selection.selected) {
        if (value.position < row.position) {
          this.selection.deselect(value);
        }
      }
    }
    this.selectedLines.emit(this.selection.selected);
  }

  nextLineIsOverLapTime(row: AudioSource) {
    const nextLine = this.dataSource.data.filter(
      (irow) => irow.position === row.position + 1
    );
    if (nextLine.length && this.overlapDateTime(row, nextLine[0])) {
      this.selection.select(nextLine[0]);
    }
  }

  checkLineBeforeAndAfter(row: AudioSource) {
    const lineBefore = this.dataSource.data.filter(
      (irow) => irow.position === row.position - 1
    );
    const lineAfter = this.dataSource.data.filter(
      (irow) => irow.position === row.position + 1
    );
    const lineBeforeSelected = this.selection.isSelected(lineBefore[0]);
    const lineAfterSelected = this.selection.isSelected(lineAfter[0]);
    return {
      lineBeforeSelected,
      lineAfterSelected,
    };
  }

  openConfirmationDialog(row: AudioSource) {
    this.callbackDownload.emit(row);
  }

  isDownloadDisabled(audioSourceType: string): boolean {
    // List of types that must have the download disabled
    const songTypes = ['song', 'm'];

    if (!audioSourceType) {
      return false;
    }
    const type = audioSourceType.toLowerCase();
    return songTypes.includes(type);
  }


  checkNameButton(download: AudioSource) {
    return download.linkDownload != null && download.linkDownload.length > 1 ? 'Get Files' :
      download.linkDownload != null && download.linkDownload.length === 1
        ? 'Get File' : 'Download';
  }

  private isSubSetOf(lineSource: AudioSource, line: StartAndEndDate) {
    return moment(lineSource.startTime).isSameOrAfter(line.startDateTime) &&
      moment(lineSource.endTime).isSameOrBefore(line.endDateTime);

  }

  private overlapDateTime(lineSource: AudioSource, line: AudioSource) {
    return moment(lineSource.startTime).isSameOrBefore(line.endTime) && moment(line.startTime).isSameOrBefore(lineSource.endTime);
  }
}

